import React from 'react'
import clsx from 'clsx'
import { makeStyles, createStyles, Typography } from '@material-ui/core'
import { useTranslation } from 'react-i18next'

import Price from './Price'
import {
  PRICE_WITHOUT_DISCOUNT,
  DISCOUNTED_PRICE_ONLY,
  DISCOUNTED_PRICE_WITH_DETAILS,
  isSecondHandOnly,
} from '../../utils/constants'
import CMSText from '../Global/CMSText'
import { PrismicGeneralData } from '../../interfaces/common/prismicGeneralConfig'
import { PrismicProduct } from '../../interfaces/common/prismicProduct'
import SecondHandBackInStockAlertButton from '../BackInStockAlert/SecondHandBackInStockAlertButton'
import { Variant } from '../../typings/base'
import { numberToPrice, getProductTaxonPromotion } from '../../utils/utils'
import TaxonPromotionBadge from './TaxonPromotionBadge'

export const useStyles = makeStyles((theme) =>
  createStyles({
    oldPrice: {
      textDecoration: 'line-through',
    },
    marginLeft: {
      marginLeft: theme.spacing(1),
    },
    prices: {
      display: 'flex',
      gap: theme.spacing(1),
      alignItems: 'center',
    },
    pricesOriginal: {
      fontSize: 12,
      paddingLeft: theme.spacing(1),
      borderLeft: `1px solid ${theme.palette.common.black}`,
    },
    startPrice: {
      paddingRight: theme.spacing(0.5),
    },
    startPriceAfter: {
      marginLeft: '4px',
    },
    unavailableContainer: {
      display: 'flex',
      flexDirection: 'row',
    },
  })
)

type TProps = {
  promotions: any
  settings: PrismicGeneralData
  priceMin?: number
  takeBackPrice?: number
  discountPriceFormat?: string
  selectedTakeBackOrRental?: number
  isBuyableProduct: boolean
  isDisplayPercentage?: boolean
  classe: string
  takeback?: any
  selectedQuality?: string
  originalPrice?: number
  productSettings?: PrismicProduct
  takeBackPriceMin?: number
  takeBackAvailability?: boolean
  isSecondHand?: boolean
  avalableForRentalAndSecondHand?: boolean
  isSecondHandTaxon?: boolean
  variants?: Variant[]
  afterCurrency?: string
  hideOriginalPrice?: boolean
  productTaxons?: { main: string; others?: string[] | null }
}

const ProductPriceFormat = ({
  promotions,
  priceMin: productPrice,
  takeBackPrice,
  selectedTakeBackOrRental,
  settings,
  isBuyableProduct,
  discountPriceFormat,
  isDisplayPercentage = false,
  classe,
  takeback,
  selectedQuality,
  originalPrice,
  productSettings,
  takeBackPriceMin,
  takeBackAvailability,
  isSecondHand,
  avalableForRentalAndSecondHand,
  isSecondHandTaxon,
  variants,
  afterCurrency,
  hideOriginalPrice = false,
  productTaxons,
}: TProps) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const globalPromotion = promotions?.currentPromotion
  const taxonPromotion = getProductTaxonPromotion(promotions, productTaxons)
  const currentPromotion = globalPromotion ?? taxonPromotion
  const hasTaxonPromotion = !globalPromotion && taxonPromotion !== null
  const displayFlexPrice = settings?.display_flex_price_a_day
  const isPerMonth = settings?.show_price_per_month

  const priceMin = productPrice * (isPerMonth ? 30 : 1)
  const hideOriginalPriceThreshold =
    productSettings?.data?.hide_original_price_threshold

  if (isSecondHandTaxon === false) {
    // if we are in rental taxon and we have stock both (rental + 2ndhand)
    isBuyableProduct = avalableForRentalAndSecondHand
      ? !avalableForRentalAndSecondHand
      : false
  }

  const noPromoOrSecondHand =
    (currentPromotion == null || isSecondHand) && !selectedTakeBackOrRental

  const discountedPrice = currentPromotion
    ? priceMin - (priceMin * currentPromotion) / 100
    : priceMin

  const renderPromoPercentBadge = () => (
    <TaxonPromotionBadge
      percent={currentPromotion}
      inline
      display={isDisplayPercentage}
    />
  )

  const regularPriceElement = (
    <span className={`${classe}__amount`}>
      <Price
        amount={priceMin}
        dayOrMonth={settings?.day_or_month}
        dayOrMonthShort={settings?.day_or_month_short}
        showDayOrMonth
        isShort={false}
        buyAble={isBuyableProduct}
        displayFlexPrice={displayFlexPrice}
        afterCurrency={afterCurrency}
      />
    </span>
  )

  const promotionPriceElement = isDisplayPercentage &&
    currentPromotion !== null && (
      <>
        <span className={clsx(`${classe}__oldPrice`, classes.oldPrice)}>
          <Price
            amount={priceMin}
            dayOrMonth={settings?.day_or_month}
            dayOrMonthShort={settings?.day_or_month_short}
            showDayOrMonth={false}
            buyAble={isBuyableProduct}
            displayFlexPrice={displayFlexPrice}
          />
        </span>
        <span className={clsx(`${classe}__amount`, classes.marginLeft)}>
          <Price
            amount={discountedPrice}
            dayOrMonth={settings?.day_or_month}
            dayOrMonthShort={settings?.day_or_month_short}
            showDayOrMonth
            buyAble={isBuyableProduct}
            displayFlexPrice={displayFlexPrice}
          />
        </span>
        {renderPromoPercentBadge()}
      </>
    )

  const soldOutElement = (
    <div>
      <Typography variant="h4" className="product__unavailable">
        <CMSText
          asText
          data={productSettings?.data?.sold_out}
          defaultText={t('texts:packs:victim_of_success')}
        />
      </Typography>
      {isSecondHandOnly && takeback?.data?.take_back_stock_alert_enable && (
        <SecondHandBackInStockAlertButton
          prismicTakeback={takeback?.data}
          allVariantsNotInStock={variants}
          displayTextBeforeButton={false}
        />
      )}
    </div>
  )

  return (
    <>
      {noPromoOrSecondHand &&
        (priceMin !== 0
          ? currentPromotion === null
            ? regularPriceElement
            : promotionPriceElement
          : soldOutElement)}
      {!isSecondHand &&
        currentPromotion !== null &&
        currentPromotion &&
        !hasTaxonPromotion &&
        discountPriceFormat === PRICE_WITHOUT_DISCOUNT &&
        !selectedTakeBackOrRental && (
          <span className={`${classe}__amount`}>
            <Price
              amount={priceMin}
              dayOrMonth={settings?.day_or_month}
              dayOrMonthShort={settings?.day_or_month_short}
              showDayOrMonth
              isShort={false}
              buyAble={isBuyableProduct}
              displayFlexPrice={displayFlexPrice}
            />
          </span>
        )}
      {!isSecondHand &&
        currentPromotion !== null &&
        currentPromotion &&
        !hasTaxonPromotion &&
        discountPriceFormat === DISCOUNTED_PRICE_ONLY &&
        !selectedTakeBackOrRental && (
          <span className={`${classe}__amount`}>
            <Price
              amount={discountedPrice}
              dayOrMonth={settings?.day_or_month}
              dayOrMonthShort={settings?.day_or_month_short}
              showDayOrMonth
              buyAble={isBuyableProduct}
              displayFlexPrice={displayFlexPrice}
            />
          </span>
        )}
      {!isSecondHand &&
        currentPromotion !== null &&
        currentPromotion !== undefined &&
        (discountPriceFormat === DISCOUNTED_PRICE_WITH_DETAILS ||
          hasTaxonPromotion) &&
        !selectedTakeBackOrRental && (
          <>
            <span className={clsx(`${classe}__oldPrice`, classes.oldPrice)}>
              <Price
                amount={priceMin}
                dayOrMonth={settings?.day_or_month}
                dayOrMonthShort={settings?.day_or_month_short}
                showDayOrMonth={false}
                buyAble={isBuyableProduct}
                displayFlexPrice={displayFlexPrice}
              />
            </span>
            <span className={clsx(`${classe}__amount`, classes.marginLeft)}>
              <Price
                amount={discountedPrice}
                dayOrMonth={settings?.day_or_month}
                dayOrMonthShort={settings?.day_or_month_short}
                showDayOrMonth
                buyAble={isBuyableProduct}
                displayFlexPrice={displayFlexPrice}
              />
            </span>
            {renderPromoPercentBadge()}
          </>
        )}
      {selectedTakeBackOrRental === 1 && (
        <div
          className={clsx(
            `${classe}__prices`,
            selectedQuality !== '' && classes.prices
          )}
        >
          <>
            {selectedQuality !== '' ? (
              <>
                {!takeBackAvailability && (
                  <span className={classes.startPrice}>
                    <CMSText
                      asText
                      data={takeback?.data?.price_start}
                      defaultText={t('texts:takeBack:price_start')}
                    />
                  </span>
                )}
                {currentPromotion ? (
                  <>
                    <span
                      className={clsx(`${classe}__oldPrice`, classes.oldPrice)}
                    >
                      <Price
                        amount={takeBackPrice}
                        showDayOrMonth={false}
                        isShort={false}
                        buyAble={isBuyableProduct}
                      />
                    </span>
                    <span
                      className={clsx(`${classe}__amount`, classes.marginLeft)}
                    >
                      <Price
                        amount={
                          takeBackPrice -
                          (takeBackPrice * currentPromotion) / 100
                        }
                        showDayOrMonth={false}
                        isShort={false}
                        buyAble={isBuyableProduct}
                      />
                      {afterCurrency}
                    </span>
                    {renderPromoPercentBadge()}
                  </>
                ) : (
                  <span className={`${classe}__amount`}>
                    <Price
                      amount={takeBackPrice}
                      showDayOrMonth={false}
                      isShort={false}
                      buyAble={isBuyableProduct}
                    />
                    {afterCurrency}
                  </span>
                )}
              </>
            ) : (
              <span className={`${classe}__price--start`}>
                <span className={classes.startPrice}>
                  <CMSText
                    asText
                    data={takeback?.data?.price_start}
                    defaultText={t('texts:takeBack:price_start')}
                  />
                </span>
                <span className={`${classe}__amount`}>
                  <Price
                    amount={takeBackPriceMin}
                    showDayOrMonth={false}
                    isShort={false}
                    buyAble={isBuyableProduct}
                  />
                </span>
              </span>
            )}
          </>

          {!hideOriginalPrice &&
            Number.isFinite(originalPrice) &&
            originalPrice > 0 &&
            !(
              hideOriginalPriceThreshold !== undefined &&
              hideOriginalPriceThreshold !== null &&
              numberToPrice(originalPrice) >= hideOriginalPriceThreshold
            ) && (
              <span
                className={clsx(
                  classes.pricesOriginal,
                  `${classe}__price--original`
                )}
              >
                <span className={classes.startPrice}>
                  <CMSText
                    asText
                    data={takeback?.data?.original_price}
                    defaultText={t('texts:takeBack:original_price')}
                  />
                </span>
                <Price amount={originalPrice} buyAble />

                {process.env.STORE == 'vaude' && (
                  <span className={classes.startPriceAfter}>
                    , je inkl. 19 % MwSt.
                  </span>
                )}
              </span>
            )}
        </div>
      )}
    </>
  )
}

export default ProductPriceFormat
